<template>
    <div class="item-row px-3 py-2 d-flex align-items-center justify-content-between w-100">
        <div class="d-flex align-items-center">
            <font-awesome-icon class="mr-3" :icon="['fas', 'grip-dots-vertical']" />
            <div class="d-flex align-items-center">
                <font-awesome-icon :icon="['fa-solid', icon || '']" class="counter mr-2" />
                <div>
                    <span>{{ effectInfo?.label || 'Unknown Effect' }}</span>
                    <span v-if="effectInfo?.tags.length" class="text-muted">({{ effectInfo?.tags[0] }})</span>
                </div>
            </div>
        </div>
        <div class="d-flex justify-content-end">
            <c-button v-if="isEditable" size="icon" :icon="['far', 'pen']" :disabled="readonly" @click="handleOpen" />
            <c-button size="icon" :icon="['far', 'trash-can']" :disabled="readonly" @click="handleRemove" />
        </div>
        <image-effects-properties-modals-edit
            :is-open="isOpen"
            :item="item"
            @update="handleUpdate"
            @close="handleClose"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, ref, inject, watch, Ref } from 'vue';
import CButton from '@/components/shared/CButton';
import ImageEffectsPropertiesModalsEdit from '@/components/DesignStudio/ImageEffectsProperties/components/list/item/edit/ImageEffectsPropertiesListItemEdit.vue';
import { IMAGE_EFFECT_PRESETS, PRESET_CATEGORIES } from '@/components/DesignStudio/ImageEffectsProperties/core/presets';

import { ImageEffectType, EditableEffectTypes } from '@/typings/customilyElements/imageEffectType';
import store from '@/store';

interface Props {
    id: number;
    item: ImageEffectType;
    readonly?: boolean;
}

interface Emits {
    (event: 'update', value: ImageEffectType): void;
    (event: 'remove', value: ImageEffectType): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const isOpen = ref(false);

const openEffectId = inject<Ref<string | null>>('openEffectId', ref(null));

watch(
    () => openEffectId?.value,
    (val) => {
        if (val && val === props.item.id) {
            handleOpen();
            openEffectId!.value = null;
        }
    }
);

const effectInfo = computed(() => IMAGE_EFFECT_PRESETS.find((preset) => preset.id === props.item.presetId));
const isEditable = computed(() => EditableEffectTypes.includes(props.item.type));

const icon = computed(() => {
    const categoryFound = PRESET_CATEGORIES.find(
        (presetCategory) => presetCategory.category === effectInfo?.value?.category
    );
    return categoryFound?.icon;
});

const handleClose = () => {
    isOpen.value = false;
};

const handleOpen = () => {
    store?.dispatch('imageEffectsProperties/openAddModal');
    const effect = {
        thumbnail: effectInfo.value?.thumbnail,
        label: effectInfo.value?.label,
        tags: effectInfo.value?.tags,
        type: effectInfo.value?.effectConfig.type,
        formData: props.item,
        isFree: effectInfo.value?.isFree
    };
    store?.dispatch('imageEffectsProperties/selectImageEffect', effect);
};

const handleUpdate = (item: ImageEffectType) => {
    emit('update', item);
};

const handleRemove = () => {
    emit('remove', props.item);
};
</script>

<style scoped lang="scss">
@import 'styles/utilities/functions';
.item-row {
    cursor: pointer;
    border-bottom: 1px solid get-color('gray-light');
    .counter {
        color: #7258e0;
    }
    &:last-child {
        border-bottom: none;
    }
    &:hover {
        background: linear-gradient(90deg, #ede9fb 0%, #ebf9fa 100%);
    }
}
</style>
